import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
const privacyContent = `
Política de Privacidad

Última actualización: Enero 2025

1. Información que Recopilamos
Recopilamos únicamente la información necesaria para procesar tu compra, incluyendo tu nombre, dirección de correo electrónico y datos de pago. No almacenamos información de tarjetas de crédito directamente; todos los pagos son procesados de forma segura por nuestro proveedor de pagos.

2. Uso de la Información
Tu información personal se utiliza exclusivamente para:
• Procesar y entregar tu compra digital.
• Enviarte el producto y cualquier actualización relacionada.
• Responder a consultas de soporte.

3. Protección de Datos
Implementamos medidas de seguridad estándar de la industria para proteger tu información personal contra acceso no autorizado, alteración o destrucción.

4. No Compartimos tu Información
No vendemos, intercambiamos ni transferimos tu información personal a terceros. La única excepción son los proveedores de servicios que nos ayudan a operar nuestro negocio (procesadores de pago, plataformas de entrega digital), quienes están obligados a mantener la confidencialidad.

5. Cookies
Nuestro sitio puede utilizar cookies para mejorar tu experiencia de navegación y analizar el tráfico del sitio. Puedes desactivar las cookies en la configuración de tu navegador.

6. Tus Derechos
Tienes derecho a solicitar acceso, corrección o eliminación de tus datos personales en cualquier momento contactándonos directamente.

7. Cambios a esta Política
Nos reservamos el derecho de actualizar esta política en cualquier momento. Los cambios serán publicados en esta página.
`.trim();
const termsContent = `
Términos y Condiciones

Última actualización: Enero 2025

1. Aceptación de los Términos
Al realizar una compra en nuestro sitio, aceptas estos términos y condiciones en su totalidad. Si no estás de acuerdo con alguna parte, por favor no realices la compra.

2. Producto Digital
"El Método Express Alteraciones" es un producto digital (PDF, videos y materiales complementarios) que se entrega electrónicamente. Al completar tu compra, recibirás acceso inmediato al contenido.

3. Uso Personal
El producto es para tu uso personal únicamente. Queda prohibido:
• Redistribuir, revender o compartir el contenido.
• Reproducir el material con fines comerciales.
• Modificar o crear obras derivadas del contenido.

4. Política de Reembolso
Debido a la naturaleza digital del producto, todas las ventas son finales. Sin embargo, si experimentas problemas técnicos para acceder al contenido, trabajaremos contigo para resolverlos.

5. Resultados
Los resultados mencionados en nuestra página de ventas son ejemplos y no constituyen una garantía de ingresos. Los resultados individuales pueden variar según la dedicación, ubicación, experiencia y otros factores.

6. Propiedad Intelectual
Todo el contenido del producto, incluyendo textos, imágenes, videos y materiales, está protegido por derechos de autor. Todos los derechos están reservados.

7. Limitación de Responsabilidad
No somos responsables por decisiones comerciales tomadas basándose en el contenido del producto. El material es educativo e informativo.

8. Modificaciones
Nos reservamos el derecho de modificar estos términos en cualquier momento. Las modificaciones entrarán en vigor inmediatamente después de su publicación.
`.trim();
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="w-full" style={{
      backgroundColor: '#071325'
    }} data-id="element-14">
        <div className="max-w-3xl mx-auto px-6 py-6 pb-24" data-id="element-15">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3" data-id="element-16">
            <p className="font-body text-xs" style={{
            color: 'rgba(250, 243, 232, 0.25)'
          }} data-id="element-17">
              © 2025 El Método Express Alteraciones. Todos los derechos
              reservados.
            </p>
            <div className="flex items-center gap-4" data-id="element-18">
              <button onClick={() => setActiveModal('privacy')} className="font-body text-xs underline-offset-2 hover:underline transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-amber-400 rounded px-1" style={{
              color: 'rgba(250, 243, 232, 0.4)'
            }} data-id="element-19">
                Política de Privacidad
              </button>
              <span className="text-xs" style={{
              color: 'rgba(250, 243, 232, 0.15)'
            }} data-id="element-20">
                ·
              </span>
              <button onClick={() => setActiveModal('terms')} className="font-body text-xs underline-offset-2 hover:underline transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-amber-400 rounded px-1" style={{
              color: 'rgba(250, 243, 232, 0.4)'
            }} data-id="element-21">
                Términos y Condiciones
              </button>
            </div>
          </div>
        </div>
      </footer>

      {/* Modal overlay */}
      <AnimatePresence data-id="element-22">
        {activeModal && <motion.div className="fixed inset-0 z-[60] flex items-center justify-center p-4" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} data-id="element-23">
            {/* Backdrop */}
            <motion.div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setActiveModal(null)} initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-24" />

            {/* Modal content */}
            <motion.div className="relative w-full max-w-2xl max-h-[80vh] rounded-xl overflow-hidden shadow-2xl" style={{
          backgroundColor: '#FAF3E8'
        }} initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.25,
          ease: 'easeOut'
        }} role="dialog" aria-modal="true" aria-label={activeModal === 'privacy' ? 'Política de Privacidad' : 'Términos y Condiciones'} data-id="element-25">
              {/* Header */}
              <div className="sticky top-0 flex items-center justify-between px-6 py-4 border-b" style={{
            backgroundColor: '#0B1A2E',
            borderColor: 'rgba(212, 160, 23, 0.2)'
          }} data-id="element-26">
                <h2 className="font-serif-display font-bold text-lg" style={{
              color: '#FAF3E8'
            }} data-id="element-27">
                  {activeModal === 'privacy' ? 'Política de Privacidad' : 'Términos y Condiciones'}
                </h2>
                <button onClick={() => setActiveModal(null)} className="p-1.5 rounded-lg transition-colors hover:bg-white/10 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-amber-400" aria-label="Cerrar" data-id="element-28">
                  <X size={20} style={{
                color: '#FAF3E8'
              }} data-id="element-29" />
                </button>
              </div>

              {/* Body */}
              <div className="overflow-y-auto max-h-[calc(80vh-60px)] px-6 py-6" data-id="element-30">
                <pre className="font-body text-sm leading-relaxed whitespace-pre-wrap" style={{
              color: '#1A1A1A'
            }} data-id="element-31">
                  {activeModal === 'privacy' ? privacyContent : termsContent}
                </pre>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}